import type {
  AppSettings,
  Stage,
  Role,
  Permission,
  TeamMember,
  Product,
  Program,
  RoleKey,
} from "./types";

// ─── Version & Debug ───────────────────────────────────────────
export const DEV_VERSION = "Beta 2.56";
export const PXM_DEBUG = typeof window !== "undefined" && window.location.search.includes("debug=true");

// ─── Feature Flags ────────────────────────────────────────────
// env var for deploy-level control, query param for ad-hoc testing
const hasQueryParam = (key: string) => typeof window !== "undefined" && window.location.search.includes(`${key}=true`);
export const FEATURES = {
  variants: import.meta.env.VITE_FEATURE_VARIANTS === "true" || hasQueryParam("variants"),
} as const;

// ─── Color Constants ───────────────────────────────────────────
// Runtime values for dynamic inline styles
export const C = {
  charcoal: "#31363d",
  paper: "#FDFBFA",
  peach: "#E28C7A",
  border: "#EBE7E4",
  card: "#FFFFFF",
  mid: "#6B6863",
  light: "#8A8784",
  rose: "#D15865",
  success: "#4CAF50",
  bgRaised: "#F7F4F2",
  /* Legacy aliases — resolve to core palette */
  slate: "#31363d",
  dark: "#31363d",
  latte: "#E28C7A",
  cream: "#F7F4F2",
  lime: "#E28C7A",
  gold: "#E28C7A",
  butter: "#EBE7E4",
  gradientBlush: "linear-gradient(135deg, #E28C7A, #D15865)",
  gradientDark: "linear-gradient(135deg, #464D56, #31363d)",
} as const;

// ─── Channel Colors & Labels ──────────────────────────────────
export const CHANNEL_COLORS: Record<string, string> = {
  amazon: "#E28C7A",
  walmart: "#3C61A9",
  target: "#D15865",
  dtc: "#C9C45B",
  shopify: "#C9C45B",
};

export const CHANNEL_LABELS: Record<string, string> = {
  amazon: "Amazon",
  walmart: "Walmart",
  target: "Target",
  dtc: "DTC / Shopify",
  shopify: "DTC / Shopify",
  costco: "Costco",
  "home depot": "Home Depot",
  staples: "Staples",
};

// ─── Channel Column Mappings (for Copy Manager spreadsheet import) ───
import type { ChannelColumnMap } from "./types";

export const CHANNEL_COLUMN_MAPS: Record<string, ChannelColumnMap> = {
  amazon: {
    label: "Amazon",
    idColumn: "Item ID",
    skuColumn: "ASIN",
    titleColumn: "Amazon Title",
    descriptionColumn: "Amazon Long Copy",
    bulletColumns: ["Amazon Bullet 1", "Amazon Bullet 2", "Amazon Bullet 3", "Amazon Bullet 4", "Amazon Bullet 5"],
    detectPatterns: ["asin", "amazon"],
  },
  walmart: {
    label: "Walmart",
    idColumn: "Item ID",
    skuColumn: "UPC",
    titleColumn: "Walmart Title",
    descriptionColumn: "Walmart Long Description",
    bulletColumns: ["Walmart Key Feature 1", "Walmart Key Feature 2", "Walmart Key Feature 3", "Walmart Key Feature 4", "Walmart Key Feature 5"],
    detectPatterns: ["walmart", "upc"],
  },
  target: {
    label: "Target",
    idColumn: "Item ID",
    skuColumn: "DPCI",
    titleColumn: "Target Title",
    descriptionColumn: "Target Description",
    bulletColumns: ["Target Bullet 1", "Target Bullet 2", "Target Bullet 3", "Target Bullet 4", "Target Bullet 5"],
    detectPatterns: ["target", "dpci"],
  },
  dtc: {
    label: "DTC / Shopify",
    idColumn: "Item ID",
    skuColumn: "SKU",
    titleColumn: "Product Title",
    descriptionColumn: "Product Description",
    bulletColumns: ["Feature 1", "Feature 2", "Feature 3", "Feature 4", "Feature 5"],
    detectPatterns: ["shopify", "dtc", "product title"],
  },
};

// ─── Target Audience Options ──────────────────────────────────
export const TARGET_AUDIENCE_OPTIONS: string[] = [
  "Moms/Household Managers",
  "Student",
  "Teacher",
  "Young Professional",
  "Business Professional",
  "Home Organizer",
];

// ─── Category → Use Case Mapping ──────────────────────────────
// Source: Michelle's "Use Case - Completed.xlsx" (Product Type + Product Class sheets)
export const CATEGORY_USE_CASES: Record<string, string> = {
  "Appointment Books and Planners": "Plan classes, projects, appointments, and personal goals in one stylish dated book so busy weeks stay visible and on track.",
  "Badge Kits": "Display IDs, visitor passes, and event credentials neatly and securely for school, office, or conference use.",
  "Ballpoint": "Reliable everyday pen for notes, forms, to-do lists, and signatures with smooth, low-smudge writing.",
  "Binder Accessories": "Add dividers, tabs, and pockets that turn any binder into an easy-to-navigate system for notes, records, or projects.",
  "Binder Clips": "Keep papers, receipts, swatches, and project packets together so important sets stay sorted from desk to bag.",
  "Binders": "Create a polished home base for class notes, meeting materials, household papers, or creative projects that need structure.",
  "Board Accessory Kits": "Equip a board in one purchase with markers, magnets, erasers, and clips so it is ready for planning, posting, and brainstorming.",
  "Board Sets": "Outfit a wall or work zone with coordinated boards that make it easy to plan, pin, and share ideas in one cohesive look.",
  "Cable Management Accessory": "Secure charging cords, monitor cables, and device wires so desks stay neat, organized, and ready to work.",
  "Calculators": "Handle quick math, homework checks, budgets, and everyday calculations with tools that are easy to keep close at hand.",
  "Calendar Chalkboards": "Show monthly plans, reminders, and countdowns on a reusable calendar board that doubles as functional decor.",
  "Chalk": "Refresh chalk-based boards and activity spaces for lessons, signage, brainstorming, or outdoor creativity.",
  "Chalkboards": "Create a reusable writing surface for schedules, menus, reminders, and hand-lettered messages with a warm, decorative feel.",
  "Classroom Decor": "Add personality and polish with coordinated decor that makes learning, study, and creative spaces feel welcoming and organized.",
  "Clip Assortment Packs": "Stock a mix of clips for sorting papers, sealing snacks, organizing cords, and keeping small tasks moving.",
  "Clipboards": "Create a grab-and-go writing surface for checklists, forms, walk-throughs, or note-taking away from the desk.",
  "Color Markers": "Add bold color to notes, posters, doodles, and creative projects while keeping ideas easy to spot and share.",
  "Combo Boards": "Write reminders and pin papers in one compact spot so schedules, notes, and inspiration stay together.",
  "Combo Calendar Boards": "Track the month, pin important papers, and manage weekly priorities from one multifunction planning center.",
  "Connecting Cables/Cords": "Keep devices powered and connected with dependable cords for charging, syncing, and everyday desk productivity.",
  "Desk Pad": "Protect the desktop while creating a clean surface for writing, mouse movement, and daily organization.",
  "Desk Pad Calendars": "Map out monthly deadlines and daily priorities directly on the desk so plans stay visible while you work.",
  "Desktop and Handheld Staplers (Non-Electric)": "Fasten packets, reports, handouts, and household paperwork quickly with a dependable stapler that belongs on every desk.",
  "Document Holders": "Protect and transport important pages, forms, or artwork in a holder that keeps papers flat, clean, and easy to find.",
  "Drawer Organizer": "Divide small supplies into dedicated spaces so pens, clips, tools, and gadgets stay easy to grab and easy to put back.",
  "Dry Erase Boards": "Capture to-dos, reminders, sketches, and plans on a reusable surface that keeps ideas front and center.",
  "Dry Erase Calendar Boards": "Plan the month at a glance with a reusable board that makes deadlines, events, and routines easy to update.",
  "Dry Erase Sheets": "Turn smooth surfaces into instant write-and-wipe planning space for lists, lessons, and lightweight organization.",
  "Encased Pencils": "Write, sketch, and problem-solve with classic pencils that are easy to sharpen, share, and keep in circulation.",
  "Erasers": "Cleanly remove pencil marks so notes, sketches, and schoolwork stay neat through every revision.",
  "Expanding Files": "Sort papers by project, month, or topic in one portable file that grows with your workload.",
  "Expanding Jackets": "Carry thicker stacks of loose papers securely in an enclosed jacket that keeps documents protected on the go.",
  "Expanding-Expanding Files": "Organize high-volume paperwork into labeled sections so everything from receipts to projects stays separated and portable.",
  "Expanding-Pockets": "Store loose pages, forms, and reference sheets in flexible pockets that prevent creasing and quick-loss clutter.",
  "Expanding-Wallets": "Keep important papers contained in a slim expandable wallet that travels easily between home, school, and work.",
  "File Storage": "Store active or archive files in sturdy containers that keep paperwork protected, sorted, and easy to retrieve.",
  "Filing Kit": "Set up an instant filing system with folders, labels, and storage pieces that bring order to new or reset spaces.",
  "Folders": "Separate papers by subject, project, or priority so daily materials stay protected and easy to locate.",
  "Gel": "Write with smooth, expressive ink that makes notes, journaling, planning, and color-coding feel more elevated.",
  "Glass Dry Erase Boards": "Add a sleek, modern writing surface for plans, messages, and brainstorming that also upgrades the look of the room.",
  "Glass Dry Erase Calendar Boards": "Track monthly plans on a premium glass surface that blends organization with modern decor.",
  "Hanging File Accessories": "Support hanging-file systems with tabs, inserts, and accessories that keep drawers labeled and easy to navigate.",
  "Hanging Folders": "Create a fast, accessible filing system for projects, clients, or household paperwork inside drawers and carts.",
  "Highlighters": "Call out deadlines, key ideas, and action items so important information stands out on the page.",
  "Hole Punch": "Prep papers for binders, planners, and filing systems with a clean punch that keeps documents ready to store.",
  "Index Cards": "Capture facts, prompts, recipes, and study notes on cards that are easy to sort, shuffle, and review.",
  "Journals": "Give ideas, reflections, plans, and notes a dedicated place in a journal that feels personal and giftable.",
  "Labels": "Identify bins, folders, cords, and supplies quickly so shared spaces stay organized and intuitive to use.",
  "Lap Boards": "Create reusable individual work surfaces for practice, problem-solving, and quick check-ins without wasting paper.",
  "Legal Pads": "Capture fast notes, call points, to-do lists, and meeting takeaways in a format built for speed and clarity.",
  "Letter Sorters": "Separate mail, forms, and active paperwork into visible slots so the next step is always easy to find.",
  "Locker Boards": "Turn locker doors into mini planning stations for schedules, reminders, and personal notes between classes.",
  "Locker Cups and Bins": "Store pens, beauty items, tech extras, and daily essentials neatly inside lockers or tight storage spaces.",
  "Locker Décor": "Personalize lockers with coordinated decor that adds style without sacrificing day-to-day function.",
  "Locker Kits": "Create an organized, personalized locker in one step with matching essentials designed to work together.",
  "Locker Lights": "Brighten dim lockers and small storage spaces so essentials are easier to spot and the space feels more personal.",
  "Locker Magnets": "Post schedules, photos, and reminders securely on magnetic locker surfaces without damage or mess.",
  "Locker Mirrors": "Add a quick-check mirror that makes lockers more functional for daily routines and personal touch-ups.",
  "Locker Shelves": "Add vertical storage inside lockers so books, gear, and supplies stay separated instead of piled up.",
  "Magazine Holders": "Stand up notebooks, files, and periodicals so shelves look tidy and important materials stay easy to pull.",
  "Magnet": "Post reminders, photos, and lightweight documents on magnetic surfaces in a way that feels clean and intentional.",
  "Magnet Inserts": "Refresh or customize magnetic labels and display pieces so organization systems stay flexible and easy to update.",
  "Magnets": "Secure notes, schedules, inspiration, and mini checklists to magnetic boards and surfaces without clutter.",
  "Magnifying Glass": "Support close reading, science exploration, and detailed craft work with a tool that makes tiny details easier to see.",
  "Mechanical Pencils": "Write with consistent, fine lines for note-taking, math work, sketching, and precision tasks without constant sharpening.",
  "Mixed Accessory Kits": "Stock the desk, board, or study space with a curated assortment of essentials that work together from day one.",
  "Monitor Stands": "Lift screens to a more comfortable height while creating useful storage space underneath for a cleaner desktop.",
  "Mounting Hardware": "Hang boards, decor, and organizers securely so workspaces and rooms come together quickly and cleanly.",
  "Note Sets": "Keep coordinated note-taking and list-making tools on hand for reminders, messages, and everyday paper planning.",
  "Organizer or All in One": "Bring planning, note-taking, and storage together in one piece so everyday essentials travel and work well together.",
  "Paper Clips": "Temporarily group papers, receipts, and task bundles without damaging pages or creating bulk.",
  "Paper Trays": "Create clear in-and-out zones for mail, forms, and active papers so desks stay uncluttered and actionable.",
  "Pencil Cups": "Keep writing tools and small supplies upright and visible so the desktop feels tidy and ready to work.",
  "Pencil Erasers": "Extend the life of pencils with replacement erasers that keep writing tools ready for everyday use.",
  "Pencil Sharpeners": "Keep pencils classroom- and desk-ready with sharpeners that make quick work of daily writing and drawing needs.",
  "Pins": "Secure photos, reminders, swatches, and lightweight papers to cork and fabric surfaces with ease.",
  "Porous": "Write bold labels, posters, and creative notes with porous-point pens that balance precision with visual impact.",
  "Pouches and Cases": "Contain pens, tools, cords, and small essentials in one portable pouch so bags and desks stay organized.",
  "Power Tech Solutions": "Keep devices charged, connected, and ready with everyday power accessories that support modern work and study.",
  "Presentation Markers": "Create legible, high-impact writing for boards, flip charts, posters, and shared planning surfaces.",
  "Presentation Padfolios": "Carry notes, documents, and writing tools in one polished padfolio for meetings, interviews, or presentations.",
  "Rubber Band": "Bundle papers, cards, cords, and small items quickly with flexible bands that simplify storage and transport.",
  "Scissors": "Cut paper, labels, packaging, and craft materials cleanly for everyday office, school, and creative tasks.",
  "Storage And Organization Kits": "Refresh a space fast with coordinated storage pieces that turn scattered supplies into a system.",
  "Tack Boards": "Create a dedicated pin-up surface for reminders, inspiration, schedules, and rotating visual plans.",
  "Tacks": "Pin notes, photos, and lightweight displays securely to boards and walls where ideas need to stay visible.",
  "Tape Dispensers": "Keep tape stable, easy to grab, and always ready for wrapping, repairs, labels, and quick desk tasks.",
  "Tech Pouches and Cases": "Protect tablets, chargers, earbuds, and cables in cases designed to keep tech essentials together and travel-ready.",
  "Top Tab Folders": "File documents neatly in drawers and boxes with classic folders that support simple, scalable organization.",
  "Top Tab-Fastener Folders": "Keep punched documents secure inside folders with built-in fasteners that prevent pages from shifting or getting lost.",
  "Utility Cups": "Hold pens, markers, scissors, or beauty tools in a flexible container that works across desks, lockers, and carts.",
  "Wall Calendars": "See deadlines, appointments, and family routines at a glance with a calendar that stays visible all month long.",
  "Washi Tape": "Add decorative function to planners, gifts, boards, and craft projects with tape that is easy to place and reposition.",
  "Writing Packs or Kits": "Stock up on coordinated pens, pencils, and extras in one set that makes desk setup or gifting simple.",
};

// ─── Font Constants ────────────────────────────────────────────
export const font = {
  primary: "'Outfit', system-ui, sans-serif",
  secondary: "'Karla', system-ui, sans-serif",
} as const;

// ─── Stages ────────────────────────────────────────────────────
export const STAGES: Stage[] = [
  { key: "brief", label: "Brief", color: C.mid },
  { key: "images", label: "Images", color: C.slate },
  { key: "infographics", label: "Infographics", color: C.peach },
  { key: "review", label: "Review", color: C.slate },
  { key: "qa", label: "QA Review", color: C.mid },
  { key: "complete", label: "Complete", color: C.slate },
];

// ─── Workflow Status ────────────────────────────────────────────
export const WORKFLOW_STATUSES = [
  { key: 'draft', label: 'Draft', color: '#A8A5A0' },
  { key: 'in-progress', label: 'In Progress', color: '#E28C7A' },
  { key: 'in-review', label: 'In Review', color: '#6B6863' },
  { key: 'approved', label: 'Approved', color: '#31363d' },
  { key: 'locked', label: 'Locked', color: '#31363d' },
] as const;

// ─── Seed Data ─────────────────────────────────────────────────
export const SEED_PRODUCTS: Product[] = [];

export const SEED_PROGRAMS: Program[] = [];

// ─── Roles ─────────────────────────────────────────────────────
export const ROLES: Record<RoleKey, Role> = {
  admin: {
    label: "Admin",
    color: C.slate,
    desc: "Full access — manage team, settings, and all content",
  },
  editor: {
    label: "Editor",
    color: C.peach,
    desc: "Create, edit, generate, and approve content",
  },
  designer: {
    label: "Designer",
    color: C.mid,
    desc: "Visual design, image generation, and creative assets",
  },
  copywriter: {
    label: "Copywriter",
    color: C.border,
    desc: "Write, edit, and refine product copy and descriptions",
  },
  channel_manager: {
    label: "Channel Manager",
    color: C.peach,
    desc: "Manage retailer profiles, programs, and channel strategy",
  },
  viewer: {
    label: "Viewer",
    color: C.border,
    desc: "View-only access to programs, products, and analytics",
  },
};

// ─── Default Permissions ───────────────────────────────────────
export const DEFAULT_PERMISSIONS: Permission = {
  pipeline: {
    admin: "full",
    editor: "edit",
    designer: "view",
    copywriter: "view",
    channel_manager: "edit",
    viewer: "view",
  },
  programs: {
    admin: "full",
    editor: "edit",
    designer: "view",
    copywriter: "view",
    channel_manager: "full",
    viewer: "view",
  },
  products: {
    admin: "full",
    editor: "edit",
    designer: "edit",
    copywriter: "edit",
    channel_manager: "edit",
    viewer: "view",
  },
  copy: {
    admin: "full",
    editor: "edit",
    designer: "view",
    copywriter: "full",
    channel_manager: "full",
    viewer: "none",
  },
  "copy-gen": {
    admin: "full",
    editor: "edit",
    designer: "edit",
    copywriter: "full",
    channel_manager: "edit",
    viewer: "none",
  },
  studio: {
    admin: "full",
    editor: "edit",
    designer: "full",
    copywriter: "edit",
    channel_manager: "edit",
    viewer: "none",
  },
  qa: {
    admin: "full",
    editor: "edit",
    designer: "view",
    copywriter: "edit",
    channel_manager: "view",
    viewer: "view",
  },
  analytics: {
    admin: "full",
    editor: "view",
    designer: "view",
    copywriter: "view",
    channel_manager: "view",
    viewer: "view",
  },
  settings: {
    admin: "full",
    editor: "none",
    designer: "none",
    copywriter: "none",
    channel_manager: "none",
    viewer: "none",
  },
  team: {
    admin: "full",
    editor: "none",
    designer: "none",
    copywriter: "none",
    channel_manager: "none",
    viewer: "none",
  },
};

// ─── Team Members Seed Data ────────────────────────────────────
export const SEED_TEAM_MEMBERS: TeamMember[] = [
  {
    id: 1,
    name: "Jason Deily",
    initials: "JD",
    email: "jdeily@u-brands.com",
    role: "admin",
    title: "VP Design & Brand Marketing",
    color: C.slate,
    status: "online",
    lastActive: "Now",
    programs: 6,
    products: 12,
    isCurrentUser: true,
  },
];

// ─── Default App Settings ──────────────────────────────────────
export const DEFAULT_APP_SETTINGS: AppSettings = {
  // Visual Direction
  typographyFonts: [
    {
      role: "Primary / Headlines",
      family: "Outfit",
      fallback: "system-ui",
      weights: ["600 Semibold", "700 Bold", "800 Extra Bold"],
      sample: "The Quick Brown Fox",
    },
    {
      role: "Secondary / Body",
      family: "Karla",
      fallback: "system-ui",
      weights: ["400 Regular", "500 Medium", "600 Semibold"],
      sample: "Designed for everyday",
    },
  ],
  typographySizes: [
    { label: "Hero", size: "36px", use: "Main image headlines" },
    { label: "Heading", size: "24px", use: "Feature callouts" },
    { label: "Subhead", size: "18px", use: "Supporting text" },
    { label: "Body", size: "14px", use: "Descriptions, bullets" },
    { label: "Caption", size: "11px", use: "Legal, disclaimers" },
  ],
  typographyRules:
    "Always use sentence case for bullet points. Headlines in ALL CAPS only on hero images. Never use more than 2 font weights per infographic. Minimum font size for Amazon images: 16px.",
  photoStyle: 0,
  photoLighting: "natural",
  photoCameraAngles: ["eye-level", "45-degree"],
  photoMoodTags: ["clean", "modern", "premium"],
  photoPropTags: ["desk accessories", "plants", "marble surface"],
  photoSceneTags: ["home office", "modern desk", "styled workspace"],
  photoExamples: [],
  photoNotes:
    "Soft natural lighting preferred. Avoid harsh shadows. Product should always appear premium — no cluttered scenes. Props should complement, not compete.",
  brandColors: [
    { name: "Charcoal", hex: "#31363d", role: "Primary Dark" },
    { name: "Paper", hex: "#FDFBFA", role: "Page Background" },
    { name: "Peach", hex: "#E28C7A", role: "Warm Accent" },
    { name: "Border", hex: "#EBE7E4", role: "Borders & Dividers" },
    { name: "Rose", hex: "#D15865", role: "Error / Alert" },
  ],
  infographicActiveStyles: ["Feature callouts", "Dimension diagrams", "Comparison charts"],
  infographicNotes:
    "Max 5 callouts per infographic. Use icons to support text, not replace it. Always include product in frame — never text-only slides.",
  layoutNotes:
    "Amazon images: white background required for main image. All secondary images should tell a visual story in sequence. Target+ images follow 4:5 ratio for mobile.",
  imageRequirements: [
    {
      slot: "Hero (Main)",
      ratio: "1:1",
      minPx: "2000 x 2000",
      bg: "Pure white #FFFFFF",
      notes: "Product centered, 85% fill",
    },
    {
      slot: "Detail 2-4",
      ratio: "1:1",
      minPx: "2000 x 2000",
      bg: "Lifestyle or white",
      notes: "Feature callouts, angles",
    },
    {
      slot: "Infographic",
      ratio: "1:1",
      minPx: "2000 x 2000",
      bg: "Brand peach #E28C7A light tint",
      notes: "Text overlay with specs",
    },
    {
      slot: "Lifestyle",
      ratio: "1:1 or 16:9",
      minPx: "2000 x 2000",
      bg: "In-context",
      notes: "Show product in use",
    },
  ],
  bgColors: [
    { label: "Product Shots", hex: "#FFFFFF", name: "White" },
    { label: "Lifestyle", hex: "#FDFBFA", name: "Off-white" },
    { label: "Infographics", hex: "#F7F4F2", name: "Warm raised" },
    { label: "Comparison", hex: "#F5F3EE", name: "Warm gray" },
  ],
  layoutRules: [
    {
      title: "Product Placement",
      rules: "Center-aligned, 80-90% frame fill\nSlight angle (15 degrees) for dimension\nShadow: soft drop, 10% opacity\nNever crop product edges",
    },
    {
      title: "Text Hierarchy",
      rules: "Headline > Subhead > Body > CTA\nMax 3 text levels per image\nLeft-aligned preferred\nRight-aligned only for balance",
    },
    {
      title: "Spacing & Grid",
      rules: "12-column grid for infographics\n24px minimum gutters\nConsistent padding: 48px edges\nVisual weight: left-heavy bias",
    },
    {
      title: "Do Not",
      rules: "Stack more than 2 text blocks\nUse gradients on text\nPlace text over busy areas\nMix photography styles in a set",
    },
  ],
  colorPairingRules: [
    {
      pair: "Charcoal + Paper",
      use: "Primary CTA, headlines on light backgrounds",
      ok: true,
    },
    {
      pair: "Olive + Dark",
      use: "Secondary accents, highlight badges",
      ok: true,
    },
    {
      pair: "Blush + Sage",
      use: "Avoid — low contrast combination",
      ok: false,
    },
  ],
  elementPlacementRules: [
    {
      rule: "Text Placement",
      desc: "Top-third or bottom-third. Never over product face.",
    },
    {
      rule: "Icon Style",
      desc: "Line icons, 2px stroke, sage or dark color only.",
    },
    {
      rule: "Callout Lines",
      desc: "1px, dashed, sage green. Angled, not straight.",
    },
    {
      rule: "Safe Zone",
      desc: "15% padding from all edges. No bleed text.",
    },
  ],
  templates: [
    {
      name: "Amazon Main Image",
      type: "PSD",
      category: "Hero",
      updated: "Mar 15",
      status: "Active",
    },
    {
      name: "Feature Infographic v2",
      type: "AI",
      category: "Infographic",
      updated: "Mar 12",
      status: "Active",
    },
    {
      name: "Lifestyle Composition",
      type: "PSD",
      category: "Lifestyle",
      updated: "Mar 10",
      status: "Active",
    },
    {
      name: "Product Detail Module",
      type: "PSD",
      category: "Detail",
      updated: "Feb 28",
      status: "Draft",
    },
    {
      name: "Comparison Chart",
      type: "AI",
      category: "Infographic",
      updated: "Feb 20",
      status: "Active",
    },
  ],
  sketchExamples: [],
  // Brand Language
  brandVoice:
    "Professional yet approachable. We speak to creative professionals who value both aesthetics and function. Tone: confident, design-forward, inclusive. Avoid: corporate jargon, hyperbole, gendered language.",
  brandStory:
    "U Brands creates beautifully designed, functional products that elevate everyday workspaces. Our philosophy: good design should be accessible to everyone.",
  alwaysIncludeTags: [
    "U Brands",
    "brand name in title",
    "pack size",
    "dimensions",
    "target room/space",
  ],
  neverUseTags: ["cheap", "best", "guarantee", "#1", "competitor names"],
  // Copywriting Rules (global, categorized)
  copywritingRules: [
    {
      category: "Formatting",
      rules: [
        'Use inch symbol (") not the word "inch" — e.g. 15-16" not 15 16 inch',
        'Dimensions must always include inch marks — e.g. 24" x 36" not 24 x 36',
        "Use hyphens for ranges — e.g. 3-5 not 3 to 5",
        "Use numerals for all measurements, never spell out numbers",
      ],
    },
    {
      category: "Punctuation",
      rules: [],
    },
    {
      category: "Brand Terms",
      rules: [],
    },
    {
      category: "Prohibited Phrases",
      rules: [],
    },
  ],
  // AI Settings
  aiSliders: {
    amazon: {
      tone: 70,
      keywordDensity: 60,
      creativity: 50,
      bulletLength: 65,
      titleComplexity: 55,
    },
    walmart: {
      tone: 50,
      keywordDensity: 45,
      creativity: 35,
      bulletLength: 50,
      titleComplexity: 40,
    },
    target: {
      tone: 60,
      keywordDensity: 40,
      creativity: 55,
      bulletLength: 55,
      titleComplexity: 50,
    },
    dtc: {
      tone: 80,
      keywordDensity: 25,
      creativity: 75,
      bulletLength: 70,
      titleComplexity: 60,
    },
  },
  aiToggles: {
    amazon: {
      capBullets: true,
      brandInTitle: true,
      emDash: true,
      autoKeywords: true,
      flagOverLimit: true,
      autoQA: false,
    },
    walmart: {
      capBullets: false,
      brandInTitle: true,
      emDash: false,
      autoKeywords: true,
      flagOverLimit: true,
      autoQA: false,
    },
    target: {
      capBullets: false,
      brandInTitle: true,
      emDash: false,
      autoKeywords: true,
      flagOverLimit: true,
      autoQA: false,
    },
    dtc: {
      capBullets: false,
      brandInTitle: false,
      emDash: false,
      autoKeywords: true,
      flagOverLimit: false,
      autoQA: false,
    },
  },
  // Retailer Profiles
  retailerProfiles: {
    amazon: {
      titleMax: 200,
      bullets: 5,
      descType: "Product Description",
      bulletStyle: "ALL CAPS lead-in",
      titleFormat: "Brand + Product + Key Feature + Size",
    },
    walmart: {
      titleMax: 75,
      bullets: 4,
      descType: "Shelf Description",
      bulletStyle: "Feature → Benefit",
      titleFormat: "Brand + Product + Variant",
    },
    target: {
      titleMax: 150,
      bullets: 4,
      descType: "Long Description",
      bulletStyle: "Conversational",
      titleFormat: "Brand + Product + Lifestyle Keyword",
    },
    dtc: {
      titleMax: 120,
      bullets: 3,
      descType: "Marketing Description",
      bulletStyle: "Brand voice",
      titleFormat: "Lifestyle Keyword + Product",
    },
    shopify: {
      titleMax: 100,
      bullets: 3,
      descType: "Product Description",
      bulletStyle: "Conversational",
      titleFormat: "Brand + Product + Use Case",
    },
  },
  // Permissions
  permissions: null, // null means use DEFAULT_PERMISSIONS
  // Image Generation — hardcoded per-type configs (no per-style controls)
  imageTypeConfigs: [
    {
      type: "hero",
      label: "Hero",
      position: 1,
      enabled: true,
      description: "Clean product shot, no text overlays",
    },
    {
      type: "graphic-hero",
      label: "Graphic Hero",
      position: 2,
      enabled: true,
      description: "Hero shot with text on packaging or product surface",
    },
    {
      type: "lifestyle",
      label: "Lifestyle",
      position: 3,
      enabled: true,
      description: "Product in a real environment, editorial feel",
    },
    {
      type: "infographic",
      label: "Infographic",
      position: 4,
      enabled: true,
      description: "Feature highlight with lifestyle photography + text overlays",
    },
  ],
  // Creative Direction — controls how VD briefs are generated
  creativeDirection: {
    copyTone: ["Punchy", "Short + bold"],
    featureStyle: "rewrite-bold",
    generalNotes: "",
    slotStructure: ["hero", "lifestyle", "infographic", "infographic", "infographic", "infographic"],
  },
  // Global style settings applied to all sketch/realism images
  globalSketchStyle: "Hand-drawn pencil sketch with light watercolor washes on white paper. Loose, gestural line work with visible pencil strokes. Muted watercolor color washes, not saturated. This should look like a designer's sketchbook concept — directional, not final. Do NOT generate a photograph, 3D render, or photorealistic image.",
  globalRealismStyle: "Professional studio-quality product photography. Clean, even illumination that highlights material texture and finish. Premium e-commerce listing image quality — tactile, premium, and true to material.",
};

// ─── Creative Direction Pill Options ──────────────────────────
export const COPY_TONE_OPTIONS = [
  "Punchy", "Short + bold", "Conversational", "Minimal / clean",
  "Editorial", "Playful", "Technical / precise", "Aspirational",
];
export const FEATURE_STYLE_OPTIONS: { value: string; label: string; desc: string }[] = [
  { value: "rewrite-bold", label: "Rewrite boldly", desc: "Transform features into punchy headlines and short callouts" },
  { value: "keep-close", label: "Keep close to original", desc: "Light editing — stay faithful to the feature text" },
  { value: "minimal", label: "Minimal text", desc: "Just a short headline per image, minimal supporting copy" },
  { value: "editorial", label: "Editorial", desc: "Longer, story-driven copy that reads like a magazine feature" },
];
export const SLOT_TYPE_OPTIONS = ["hero", "graphic-hero", "lifestyle", "infographic"];


// ─── Configuration ─────────────────────────────────────────────
export const CONFIG = {
  PAGE_SIZE: 100,
  REQUEST_TIMEOUT: 8000,
  PHOTO_TIMEOUT: 30000,
  PRODUCT_TIMEOUT: 15000,
};
